// controls/number.js — version React Composant + JSON Schema native
import React, { useState, useEffect } from "react";
import CommonControls from "./inc/Common";

/**
 * Création d’un champ numérique conforme JSON Schema draft 2020-12
 */
export function createNumberField() {
  return {
    type: "number",
    title: "Nombre",
    minimum: null,
    maximum: null,
    multipleOf: null,
    "x-ui": "number",
    "x-placeholder": "",
    "x-displayWidth": "",
  };
}

/**
 * Éditeur du champ numérique (FieldEditor) — version composant React
 */
export function EditNumberField({ field, onChange }) {
  const [minimum, setMinimum] = useState(field.minimum ?? null);
  const [maximum, setMaximum] = useState(field.maximum ?? null);
  const [multipleOf, setMultipleOf] = useState(field.multipleOf ?? null);
  const [placeholder, setPlaceholder] = useState(field["x-placeholder"] || "");
  const [displayWidth, setDisplayWidth] = useState(field["x-displayWidth"] || "");

  // 🔄 Synchronisation ascendante vers FormBuilder
  useEffect(() => onChange("minimum", minimum), [minimum]);
  useEffect(() => onChange("maximum", maximum), [maximum]);
  useEffect(() => onChange("multipleOf", multipleOf), [multipleOf]);
  useEffect(() => onChange("x-placeholder", placeholder), [placeholder]);
  useEffect(() => onChange("x-displayWidth", displayWidth), [displayWidth]);

  return (
    <>
      <CommonControls field={field} onChange={onChange} />

      {/* ---- Bornes min / max ---- */}
      <div className="mb-3">
        <label className="form-label">Valeur minimale</label>
        <input
          type="number"
          className="form-control form-control-sm compact"
          placeholder="ex : 0"
          value={minimum ?? ""}
          onChange={(e) =>
            setMinimum(e.target.value === "" ? null : Number(e.target.value))
          }
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Valeur maximale</label>
        <input
          type="number"
          className="form-control form-control-sm compact"
          placeholder="ex : 100"
          value={maximum ?? ""}
          onChange={(e) =>
            setMaximum(e.target.value === "" ? null : Number(e.target.value))
          }
        />
      </div>

      {/* ---- Pas / multipleOf ---- */}
      <div className="mb-3">
        <label className="form-label">Pas (multipleOf)</label>
        <input
          type="number"
          step="any"
          className="form-control form-control-sm compact"
          placeholder="ex : 0.01"
          value={multipleOf ?? ""}
          onChange={(e) =>
            setMultipleOf(e.target.value === "" ? null : Number(e.target.value))
          }
        />
        <small className="text-muted">
          Si défini, restreint les valeurs au multiple indiqué (ex : 0.01 → 2 décimales).
        </small>
      </div>

      {/* ---- Placeholder ---- */}
      <div className="mb-3">
        <label className="form-label">Texte indicatif (placeholder)</label>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="ex : Saisissez un nombre"
          value={placeholder}
          onChange={(e) => setPlaceholder(e.target.value)}
        />
      </div>

      {/* ---- Largeur visuelle ---- */}
      <div className="mb-3">
        <label className="form-label">Largeur du champ (en caractères)</label>
        <input
          type="number"
          className="form-control form-control-sm compact"
          placeholder="ex : 10"
          value={displayWidth}
          onChange={(e) => setDisplayWidth(e.target.value)}
        />
      </div>
    </>
  );
}

/**
 * Compatibilité avec le FieldEditorRegistry
 */
export const editNumberField = (props) => <EditNumberField {...props} />;



/* =========================================================
   🧩 FRONT — rendu du champ numérique
   ========================================================= */
export const RenderNumberField = ({
  field,
  value,
  onChange,
  className,
  errorMessage   // <--- AJOUT
}) => {

  const placeholder = field["x-placeholder"] || "";
  const displayWidth = field["x-displayWidth"] || "";
  const min = field.minimum ?? undefined;
  const max = field.maximum ?? undefined;
  const step = field.multipleOf ?? "any";
  const required = field.required || false;

  // Normalisation (évite null/undefined dans les inputs)
  const safeValue = value ?? "";

  return (
    <div
      className={`
        fb-field fb-number
        ${field["x-bloc-class"] || "mb-3"}
        ${className || ""}
      `}
    >
      {/* Label */}
      {field.title && (
        <label className={`form-label ${errorMessage ? "text-danger" : ""}`}>
          {field.title}
          {required && <span className="text-danger">*</span>}
        </label>
      )}

      {/* Input number */}
      <input
        type="number"
        className={`
          form-control
          ${field["x-control-class"] || ""}
          ${errorMessage ? "is-invalid" : ""}
        `}
        {...(placeholder ? { placeholder } : {})}
        {...(min != null ? { min } : {})}
        {...(max != null ? { max } : {})}
        {...(displayWidth ? { size: displayWidth } : {})}
        step={step}
        value={safeValue}
        onChange={(e) => {
          const val = e.target.value;
          onChange?.(val === "" ? null : Number(val));
        }}
      />

      {/* Message d’erreur */}
      {errorMessage && (
        <div className="invalid-feedback d-block mt-1">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
